import * as GAME from 'vibegame';
import { RenderContext, RenderingPlugin, setCanvasElement } from 'vibegame/rendering';
import { TransformsPlugin } from 'vibegame/transforms';
import { TweenPlugin } from 'vibegame/tweening';
import { OrbitCameraPlugin } from 'vibegame/orbit-camera';
import { InputPlugin } from 'vibegame/input';
import { LineRendererPlugin } from '@blog-components/plugins';
import type { CanvasInstance, DemoConfig } from './types';

export async function initializeState(instance: CanvasInstance): Promise<void> {
  const { canvas, worldElement, config } = instance;

  const state = new GAME.State();

  state.registerPlugin(TransformsPlugin);
  state.registerPlugin(RenderingPlugin);
  state.registerPlugin(LineRendererPlugin);
  state.registerPlugin(TweenPlugin);
  state.registerPlugin(OrbitCameraPlugin);
  state.registerPlugin(InputPlugin);

  const plugins = config.createPlugin();
  if (Array.isArray(plugins)) {
    plugins.forEach((plugin) => state.registerPlugin(plugin));
  } else {
    state.registerPlugin(plugins);
  }

  await state.initializePlugins();

  worldElement.style.display = 'none';

  const rendererEntity = state.createEntity();
  state.addComponent(rendererEntity, RenderContext);
  RenderContext.hasCanvas[rendererEntity] = 1;

  const skyColor = worldElement.getAttribute('sky');
  if (skyColor) {
    const parsedColor = GAME.XMLValueParser.parse(skyColor);
    if (typeof parsedColor === 'number') {
      RenderContext.clearColor[rendererEntity] = parsedColor;
    }
  }

  setCanvasElement(rendererEntity, canvas);

  const xmlContent = `<world>${worldElement.innerHTML}</world>`;
  const parseResult = GAME.XMLParser.parse(xmlContent);

  if (parseResult.root.tagName === 'parsererror') {
    console.error('XML parsing failed for world:', worldElement);
    return;
  }

  GAME.parseXMLToEntities(state, parseResult.root);

  state.step(GAME.TIME_CONSTANTS.FIXED_TIMESTEP);

  canvas.__state__ = state;
  instance.state = state;

  config.setupUI?.(state);
}

export function initializeBlogCanvases(configs: DemoConfig[]): void {
  const configMap = new Map(configs.map((c) => [c.canvasId, c]));
  const instances: CanvasInstance[] = [];

  const worldElements = document.querySelectorAll('world');

  for (const worldElement of worldElements) {
    const canvasSelector = (worldElement as HTMLElement).getAttribute('canvas');
    if (!canvasSelector) continue;

    const canvas = document.querySelector(canvasSelector) as HTMLCanvasElement;
    if (!canvas) continue;

    const canvasId = canvasSelector.replace('#', '');
    const config = configMap.get(canvasId);
    if (!config) continue;

    instances.push({
      canvas,
      worldElement: worldElement as HTMLElement,
      config,
      isVisible: false,
    });
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const instance = instances.find((inst) => inst.canvas === entry.target);
        if (!instance) return;

        instance.isVisible = entry.isIntersecting;

        if (entry.isIntersecting && !instance.state) {
          initializeState(instance);
        }
      });
    },
    { rootMargin: '100px', threshold: 0.01 }
  );

  instances.forEach((instance) => observer.observe(instance.canvas));

  let lastTime = performance.now();
  let isRunning = true;

  const firstCanvas = instances[0]?.canvas;
  if (firstCanvas) {
    firstCanvas.__stop__ = () => {
      isRunning = false;
    };
  }

  function animate(currentTime: number) {
    if (!isRunning) return;
    requestAnimationFrame(animate);

    const deltaTime = (currentTime - lastTime) / 1000;
    lastTime = currentTime;

    for (const instance of instances) {
      if (instance.state && instance.isVisible) {
        instance.state.step(deltaTime);
      }
    }
  }

  requestAnimationFrame(animate);
}
